import { Alert, Button, Checkbox, Field, Modal } from '@catering/ui';
import { type FormEvent, type JSX, useState } from 'react';

import { ZONES, ZONE_LABELS, type Zone } from '../../../../customers/api';
import type { OperatingUnitOption } from '../../../../operating-units/operating-units-api';
import type { RoleOption, UpdateUserInput, UserListItem } from '../../../../users/api';
import { seesAllOperatingUnits } from '../../../../users/operating-unit-scope';
import { OperatingUnitsFieldset } from './OperatingUnitsFieldset';

export interface UserEditModalProps {
  user: UserListItem;
  roles: RoleOption[];
  actorPermissions: string[];
  unitOptions?: OperatingUnitOption[];
  canEditRoles: boolean;
  submittingProfile: boolean;
  submittingRoles: boolean;
  profileError?: string;
  rolesError?: string;
  onCancel: () => void;
  onSubmitProfile: (changes: UpdateUserInput) => void;
  onSubmitRoles: (roleIds: string[]) => void;
}

const CANNOT_GRANT_REASON = 'No podés otorgar este rol: no tenés todos sus permisos';

function cannotGrant(role: RoleOption, actorPermissions: string[]): boolean {
  const actorSet = new Set(actorPermissions);
  return role.permissions.some((code) => !actorSet.has(code));
}

/**
 * `internal-users/spec.md` → asignación de roles / edición de datos;
 * `design.md` §9.4: one modal, two independently-dirty sections, two
 * mutations (`PATCH /users/:id`, `PATCH /users/:id/roles`) fired only for
 * the section that actually changed — same `changes` pattern as
 * `EditPortalAccessModal`. `email` never renders as editable here.
 */
export function UserEditModal({
  user,
  roles,
  actorPermissions,
  unitOptions = [],
  canEditRoles,
  submittingProfile,
  submittingRoles,
  profileError,
  rolesError,
  onCancel,
  onSubmitProfile,
  onSubmitRoles,
}: UserEditModalProps): JSX.Element {
  const [fullName, setFullName] = useState(user.fullName);
  const [zones, setZones] = useState<Zone[]>([]);
  const initialUnitIds = user.operatingUnits.map((unit) => unit.id);
  const [operatingUnitIds, setOperatingUnitIds] = useState<string[]>(initialUnitIds);
  const [roleIds, setRoleIds] = useState<string[]>(
    roles.filter((role) => user.roleCodes.includes(role.code)).map((role) => role.id),
  );

  function toggleZone(zone: Zone): void {
    setZones((current) =>
      current.includes(zone) ? current.filter((z) => z !== zone) : [...current, zone],
    );
  }

  function toggleOperatingUnit(id: string): void {
    setOperatingUnitIds((current) =>
      current.includes(id) ? current.filter((value) => value !== id) : [...current, id],
    );
  }

  function toggleRole(roleId: string): void {
    setRoleIds((current) =>
      current.includes(roleId) ? current.filter((id) => id !== roleId) : [...current, roleId],
    );
  }

  function handleSubmit(event: FormEvent): void {
    event.preventDefault();

    const unitsChanged =
      JSON.stringify([...initialUnitIds].sort()) !== JSON.stringify([...operatingUnitIds].sort());
    const profileChanges: UpdateUserInput = {
      ...(fullName === user.fullName ? {} : { fullName }),
      ...(unitsChanged ? { operatingUnitIds } : {}),
    };

    if (Object.keys(profileChanges).length > 0) {
      onSubmitProfile(profileChanges);
    }

    const currentRoleIds = roles
      .filter((role) => user.roleCodes.includes(role.code))
      .map((role) => role.id)
      .sort();
    const nextRoleIds = [...roleIds].sort();

    if (canEditRoles && JSON.stringify(currentRoleIds) !== JSON.stringify(nextRoleIds)) {
      onSubmitRoles(roleIds);
    }
  }

  return (
    <Modal open onClose={onCancel} title={`Editar a ${user.fullName}`} className="max-w-lg">
      <form onSubmit={handleSubmit} className="flex flex-col gap-4" noValidate>
        {profileError !== undefined ? <Alert tone="danger">{profileError}</Alert> : null}
        {rolesError !== undefined ? <Alert tone="danger">{rolesError}</Alert> : null}

        <Field
          label="Nombre completo"
          name="fullName"
          required
          value={fullName}
          onChange={(event) => setFullName(event.target.value)}
        />

        <fieldset className="flex flex-col gap-2">
          <legend className="text-sm font-medium text-slate-900">Zonas</legend>
          {ZONES.map((zone) => (
            <Checkbox
              key={zone}
              label={ZONE_LABELS[zone]}
              checked={zones.includes(zone)}
              onChange={() => toggleZone(zone)}
            />
          ))}
          <p className="text-xs text-slate-500">Si no marcás ninguna, trabaja con todas las zonas.</p>
        </fieldset>

        <OperatingUnitsFieldset
          options={unitOptions}
          selectedIds={operatingUnitIds}
          onToggle={toggleOperatingUnit}
        />

        {seesAllOperatingUnits(
          {
            roleCodes: user.roleCodes,
            operatingUnits: operatingUnitIds.map((id) => ({ id, name: id })),
          },
          unitOptions.length,
        ) ? (
          <Alert tone="warning">Ve todos los puntos</Alert>
        ) : null}

        <fieldset className="flex flex-col gap-2">
          <legend className="text-sm font-medium text-slate-900">Roles</legend>
          {roles.map((role) => {
            const disabled = !canEditRoles || cannotGrant(role, actorPermissions);

            return (
              <Checkbox
                key={role.id}
                label={role.name}
                checked={roleIds.includes(role.id)}
                onChange={() => toggleRole(role.id)}
                disabled={disabled}
                title={disabled && canEditRoles ? CANNOT_GRANT_REASON : undefined}
              />
            );
          })}
        </fieldset>

        <div className="mt-2 flex items-center gap-3">
          <Button type="submit" loading={submittingProfile || submittingRoles}>
            Guardar cambios
          </Button>
          <Button type="button" variant="secondary" onClick={onCancel}>
            Cancelar
          </Button>
        </div>
      </form>
    </Modal>
  );
}
